import React from 'react';
import { Button, Space, Typography, Modal, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReceiptPutaway } from '@contexts/operator/ReceiptPutawayContext';

const { Text } = Typography;

export default function PutawayFooter({ onAfterSubmit }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, derived, submit } = useReceiptPutaway();

  const submittable = derived.readyItemCount > 0 && !state.submitting;

  const willLeaveResidual = () => {
    // 只計入 resolved shelfId 的 rows，與 context.submit 的送出條件一致，
    // 避免「打了 location 但沒選到 shelf」被誤判為已分配、跳過殘量確認。
    return state.items.some(item => {
      const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
      if (remaining <= 0) return false;
      const rows = state.rowStates[item.id] || [];
      const sumQty = rows.reduce((s, r) => s + (r.shelfId ? Number(r.quantity || 0) : 0), 0);
      return sumQty < remaining;
    });
  };

  const doSubmit = async () => {
    let payload;
    try {
      payload = await submit();
    } catch (err) {
      // 之前是直接 message.error('common.network_error') — 任何例外（包含程式 bug
      // 如 TypeError、JSON parse 失敗）都被當網路錯誤，operator 通報「網路錯誤」、
      // 工程師檢查網路又是好的，永遠找不到 root cause。改為 console.error 留下原始
      // 例外給 devtools / Sentry 抓得到，user message 用通用字串避免誤導。
      // eslint-disable-next-line no-console
      console.error('[PutawayFooter] submit failed:', err);
      message.error(t('common.submit_failed', { defaultValue: '送出失敗，請稍後再試或聯絡客服' }));
      return;
    }
    if (payload.failure_count > 0) {
      message.warning(t('operator.pages.putaway.submit.partial_failure', {
        success: payload.success_count, failure: payload.failure_count
      }));
    } else if (payload.success_count > 0) {
      message.success(t('operator.pages.putaway.submit.success', { count: payload.success_count }));
    } else {
      // 後端回 0/0 — 視為 no-op，讓使用者知道沒東西被送出（避免零回饋）
      message.info(t('operator.pages.putaway.submit.nothing_to_submit', {
        defaultValue: '沒有可送出的資料',
      }));
    }
    onAfterSubmit?.(payload);
  };

  const handleClick = () => {
    if (willLeaveResidual()) {
      Modal.confirm({
        title: t('operator.pages.putaway.warnings.residual_title'),
        content: t('operator.pages.putaway.warnings.residual_body'),
        okText: t('common.button.confirm'),
        cancelText: t('common.button.cancel'),
        onOk: doSubmit,
      });
    } else {
      doSubmit();
    }
  };

  return (
    <div style={{
      position: 'sticky',
      bottom: 0,
      zIndex: 5,
      borderTop: '1px solid #f0f0f0',
      background: '#fff',
      padding: '12px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.04)',
    }}>
      <Text type={submittable ? 'secondary' : 'secondary'} style={{ fontSize: 13 }}>
        {t('operator.pages.putaway.footer.ready', {
          items: derived.readyItemCount,
          qty: derived.readyQtyTotal,
        })}
      </Text>
      <Space>
        <Button onClick={() => navigate(`/inbounds/${state.receipt?.inbound_id}`)}>
          {t('common.button.cancel')}
        </Button>
        <Button
          type="primary"
          loading={state.submitting}
          disabled={!submittable}
          onClick={handleClick}
          size="large"
        >
          {t('operator.pages.putaway.footer.submit', {
            items: derived.readyItemCount,
            qty: derived.readyQtyTotal,
          })}
        </Button>
      </Space>
    </div>
  );
}
